import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import '../styles/SourceIcon.css';

const POPUP_WIDTH = 300;

const SourceIcon = observer(({ sourceIds, store }) => {
  const [popupIndex, setPopupIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupStyle, setPopupStyle] = useState({ position: 'fixed', top: 0, left: 0, zIndex: 9999, visibility: 'hidden' });
  const iconRef = useRef(null);
  const popupRef = useRef(null);
  const hideTimerRef = useRef(null);

  const computePopupStyle = useCallback((popupHeight = 0) => {
    if (!iconRef.current) return {};
    const rect = iconRef.current.getBoundingClientRect();

    // Default: open below and aligned left with the icon (1px gap so cursor can reach popup)
    let top = rect.bottom + 1;
    let left = rect.left - 4;

    // Flip left if popup would overflow right edge of viewport
    if (left + POPUP_WIDTH > window.innerWidth) {
      left = rect.right - POPUP_WIDTH;
    }

    // Flip up if popup would overflow bottom edge of viewport
    if (popupHeight > 0 && rect.bottom + popupHeight + 1 > window.innerHeight) {
      top = rect.top - popupHeight - 1;
    }

    return { position: 'fixed', top, left, zIndex: 9999 };
  }, []);

  // Two-pass: render hidden first, measure, then reposition
  useEffect(() => {
    if (showPopup && popupRef.current) {
      const height = popupRef.current.getBoundingClientRect().height;
      setPopupStyle(computePopupStyle(height));
    }
  }, [showPopup, popupIndex, computePopupStyle]);

  // Clear any pending hide timer on unmount
  useEffect(() => () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); }, []);

  // Hooks must all be called before any early returns (Rules of Hooks)
  const scheduleHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setShowPopup(false);
      if (store) store.clearHighlight();
    }, 300);
  }, [store]);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  if (!sourceIds || sourceIds.length === 0 || !store) {
    return null;
  }

  const currentSourceId = sourceIds[popupIndex];
  const source = store.getSource(currentSourceId);

  if (!source) {
    return null;
  }

  // Add numeric suffix to duplicate titles for disambiguation
  const getDisplayTitle = () => {
    if (sourceIds.length === 1) {
      return source.title;
    }

    // Get all sources and their titles
    const allSources = sourceIds.map(id => store.getSource(id)).filter(s => s);
    const titleCounts = new Map();

    // Count occurrences of each title
    allSources.forEach(s => {
      const count = titleCounts.get(s.title) || 0;
      titleCounts.set(s.title, count + 1);
    });

    // If current title is unique, no suffix needed
    if (titleCounts.get(source.title) === 1) {
      return source.title;
    }

    // Title appears multiple times - add suffix based on position
    // Count how many times we've seen this title before current index
    let seenCount = 0;
    for (let i = 0; i <= popupIndex; i++) {
      const s = allSources[i];
      if (s && s.title === source.title) {
        seenCount++;
      }
    }

    return `${source.title} (${seenCount})`;
  };

  const displayTitle = getDisplayTitle();

  // Extract favicon URL from source
  const getFaviconUrl = (url) => {
    if (!url) return null;

    try {
      // For HTTP(S) URLs, use Google's favicon service
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=16`;
      }

      // For known plex ID types, use custom icons (could expand this)
      if (url.startsWith('chembl:')) return '🧪'; // Chemistry
      if (url.startsWith('uniprot:')) return '🧬'; // Protein
      if (url.startsWith('pubmed:')) return '📄'; // Publication
      if (url.startsWith('entrezgene:')) return '🧬'; // Gene

      return null;
    } catch {
      return null;
    }
  };

  const faviconUrl = getFaviconUrl(source.url);

  const handleMouseEnter = () => {
    cancelHide();
    if (!showPopup) {
      // Show hidden first so useEffect can measure and position
      setPopupStyle({ position: 'fixed', top: 0, left: 0, zIndex: 9999, visibility: 'hidden' });
      setShowPopup(true);
    }
    store.highlightSource(sourceIds);
  };

  const handleMouseLeave = () => {
    scheduleHide();
  };

  const handlePopupMouseEnter = () => {
    cancelHide();
  };

  const handlePopupMouseLeave = () => {
    scheduleHide();
  };

  const cycleNext = (e) => {
    e.stopPropagation();
    const nextIndex = (popupIndex + 1) % sourceIds.length;
    setPopupIndex(nextIndex);
    store.highlightSource([sourceIds[nextIndex]]);
  };

  const cyclePrev = (e) => {
    e.stopPropagation();
    const nextIndex = (popupIndex - 1 + sourceIds.length) % sourceIds.length;
    setPopupIndex(nextIndex);
    store.highlightSource([sourceIds[nextIndex]]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!showPopup) setPopupStyle({ position: 'fixed', top: 0, left: 0, zIndex: 9999, visibility: 'hidden' });
      setShowPopup(!showPopup);

      if (!showPopup) {
        store.highlightSource(sourceIds);
      } else {
        store.clearHighlight();
      }
    } else if (e.key === 'Escape') {
      setShowPopup(false);
      store.clearHighlight();
    } else if (showPopup && sourceIds.length > 1) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (popupIndex + 1) % sourceIds.length;
        setPopupIndex(nextIndex);
        store.highlightSource([sourceIds[nextIndex]]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const nextIndex = (popupIndex - 1 + sourceIds.length) % sourceIds.length;
        setPopupIndex(nextIndex);
        store.highlightSource([sourceIds[nextIndex]]);
      }
    }
  };

  return (
    <span
      ref={iconRef}
      className="source-reference-icon"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      role="button"
      title=""
    >
      {sourceIds.length > 1 ? `🔗 +${sourceIds.length - 1}` : '🔗'}

      {showPopup && createPortal(
        <div
          ref={popupRef}
          className="source-popup"
          style={popupStyle}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          <a
            className="source-title"
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
          >
            {faviconUrl && (
              typeof faviconUrl === 'string' && faviconUrl.startsWith('http') ? (
                <img
                  src={faviconUrl}
                  alt=""
                  className="source-favicon"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span className="source-favicon-emoji">{faviconUrl}</span>
              )
            )}
            {displayTitle}
          </a>
          {sourceIds.length > 1 && (
            <div className="nav-buttons">
              <button
                onClick={cyclePrev}
                className="nav-button"
                aria-label="Previous source"
              >
                &lt;
              </button>
              <button
                onClick={cycleNext}
                className="nav-button"
                aria-label="Next source"
              >
                &gt;
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </span>
  );
});

export default SourceIcon;